import { DIVISION_OPTIONS } from '../constants/divisions';

/**
 * Nomenclature standard des directives : DDC-<discipline>-<NN>.
 *   ex. DDC-A-07 (Architecture), DDC-ME-04 (CVAC)
 *
 * Les codes de discipline viennent de DIVISION_OPTIONS (champ `code`) : une
 * seule source. Les nomenclatures historiques (« DDC-A01 », « DC-A-02 »,
 * « DDC - A03 », « E-6 ») restent lisibles par parse(), et ne sont jamais
 * reecrites.
 */

const CODES = DIVISION_OPTIONS?.map((d) => d?.code)?.filter(Boolean);

// Format standard : 2 chiffres, zero devant.
const STANDARD_RE = new RegExp(`^DDC-(${CODES?.join('|')})-\\d{2}$`);

/**
 * Cle de comparaison des doublons. EXACTEMENT l'expression de l'index unique
 * project_directives_nomenclature_norm_uidx :
 *   upper(regexp_replace(nomenclature, '[\s_-]', '', 'g'))
 * Les zeros de tete comptent (l'index ne les ignore pas).
 */
export const normalize = (value) =>
  String(value || '')?.replace(/[\s_-]/g, '')?.toUpperCase();

/**
 * Vrai si la nomenclature suit le format standard DDC-<code>-<NN>. Sert
 * uniquement a l'aide « Format suggere » : jamais a bloquer une saisie.
 */
export const isStandard = (value) => STANDARD_RE.test(String(value || '')?.trim());

/**
 * Lit une nomenclature, standard ou historique, en { code, nn } ; null si elle
 * n'en a pas la forme ou si le code n'est pas une discipline connue.
 *   « DDC-A01 » -> A 1 · « DC-A-02 » -> A 2 · « DDC - A03 » -> A 3 · « E-6 » -> E 6
 * Tout ce qui suit le numero est ignore, dont les revisions :
 *   « DDC-A-01-R1 » -> A 1 · « DDC-A-01 R2 » -> A 1
 */
export const parse = (value) => {
  const compact = String(value || '')?.toUpperCase()?.replace(/\s+/g, '');
  const match = compact?.match(/^(?:DDC|DC)?-?([A-Z]{1,2})-?0*(\d+)/);
  if (!match) return null;
  const code = match?.[1];
  if (!CODES?.includes(code)) return null;
  return { code, nn: parseInt(match?.[2], 10) };
};

/** Nomenclature standard : format('E', 6) -> « DDC-E-06 ». */
export const format = (code, nn) => `DDC-${code}-${String(nn)?.padStart(2, '0')}`;

/** Code de discipline d'une division (« Mechanical » -> « M »), ou null. */
export const codeOfDivision = (divisionValue) =>
  DIVISION_OPTIONS?.find((d) => d?.value === divisionValue)?.code || null;

/**
 * Prochaine nomenclature libre du projet pour la discipline de la division :
 * plus grand NN existant de ce code (via parse, historiques compris) + 1.
 * null si la division n'a pas de code.
 */
export const nextNomenclature = (directives, divisionValue) => {
  const code = codeOfDivision(divisionValue);
  if (!code) return null;
  const max = (directives || [])?.reduce((acc, d) => {
    const parsed = parse(d?.nomenclature);
    return parsed?.code === code && parsed?.nn > acc ? parsed?.nn : acc;
  }, 0);
  return format(code, max + 1);
};
